import { isNil } from 'lodash-es';

import { format, parseISO } from 'date-fns';
import { formatPrice } from 'utils';
import { TCountryCode } from 'interfaces';
import { Product, Transfer2 } from 'services/BackendApi/types';
import { IAncillaryItemData } from 'services/BackendApi/types/BBAncillaryResponse';
import { EAncillaryFlexibleAttributeType } from 'services/BookingManagerApi/types/AncillaryService';

export const ANCILLARY_ITEM_GENERIC_IDENTIFIER = 'ancillaryItem';

const ANCILLARY_DATE_FORMAT = 'dd MMM yy';

export const ANCILLARY_OPTION_SEPARATOR = ' · ';

const ANCILLARY_VARIANT_SEPARATOR = ', ';

export type TAncillaryBreakdownItem = Pick<Transfer2, 'ancillaryData' | 'customData'>;

export interface IAncillaryOption {
  label: string;
  value: string;
  isBoolean: boolean;
}

export interface IAncillaryDateTime {
  date: string;
  time?: string;
}

export interface IAncillaryNamedItem {
  name: string;
  baseProductName?: string;
  variantName?: string;
  units: number;
}

export const isAncillaryProduct = (product?: Pick<Product, 'options'>): boolean =>
  product?.options?.genericIdentifier === ANCILLARY_ITEM_GENERIC_IDENTIFIER;

export const filterOutAncillaryProductSets = <TProductSet extends { products?: Product[] }>(
  productSets: TProductSet[],
  ancillaryProductUuids: Set<string | undefined>
): TProductSet[] =>
  productSets.filter(
    productSet =>
      !isAncillaryProduct(productSet.products?.[0]) && !ancillaryProductUuids.has(productSet.products?.[0]?.uuid)
  );

export const getAncillaryRequestIndex = (item: TAncillaryBreakdownItem): number =>
  (item.ancillaryData?.userSelections?.ancillaryRequestIndex ?? item.customData?.ancillaryRequestIndex) as number;

export const toAncillaryOptions = (item: TAncillaryBreakdownItem): IAncillaryOption[] => {
  const selections = item.ancillaryData?.userSelections?.flexibleAttributesNonValidatedSelections ?? [];
  const attributes = item.ancillaryData?.product?.flexibleAttributes ?? [];

  return selections.reduce<IAncillaryOption[]>((accumulator, selection) => {
    const attribute = attributes.find(candidate => candidate.id === selection.id);

    if (isNil(attribute)) {
      return accumulator;
    }

    const isBoolean = attribute.type === EAncillaryFlexibleAttributeType.OPTION_BOOLEAN;
    const value = selection.selectedValue?.value ?? '';

    if (isBoolean && value !== 'true') {
      return accumulator;
    }

    return [...accumulator, { label: attribute.name, value, isBoolean }];
  }, []);
};

export const buildDateTimeLabel = (item: IAncillaryDateTime): string =>
  [item.date ? format(parseISO(item.date), ANCILLARY_DATE_FORMAT) : null, item.time]
    .filter(part => !isNil(part) && part !== '')
    .join(', ');

export const buildDateTimePrefix = (item: IAncillaryDateTime): string => {
  const dateAndTime = buildDateTimeLabel(item);

  return dateAndTime === '' ? '' : `${dateAndTime}.`;
};

export const buildGuestsCountLine = (numberOfAdults: number, numberOfChildren: number): string => {
  const adults = `${numberOfAdults} ${numberOfAdults === 1 ? 'Adult' : 'Adults'}`;
  const children = numberOfChildren > 0 ? `, ${numberOfChildren} ${numberOfChildren === 1 ? 'child' : 'children'}` : '';

  return `${adults}${children}`;
};

export const getAncillaryProductName = (
  item: IAncillaryNamedItem,
  variantSeparator: string = ANCILLARY_VARIANT_SEPARATOR
): string => `${item.baseProductName ?? item.name}${item.variantName ? `${variantSeparator}${item.variantName}` : ''}`;

export const formatAncillaryTitle = (item: IAncillaryNamedItem, variantSeparator?: string): string =>
  `${item.units > 1 ? `${item.units} x ` : ''}${getAncillaryProductName(item, variantSeparator)}`;

export const buildOptionsLine = (options: IAncillaryOption[], separator: string = ANCILLARY_OPTION_SEPARATOR): string =>
  options.map(option => (option.isBoolean ? option.label : `${option.label}: ${option.value}`)).join(separator);

export interface IPotentialBookingAncillaryItem {
  title: string;
  product?: Product;
  total?: string | null;
  totalCents?: number | null;
  isOnRequest?: boolean;
  cancellationPolicy?: string | null;
  ancillaryData?: IAncillaryItemData;
}

export interface IAddedAncillaryItem {
  key: string;
  name: string;
  units: number;
  total: string | null;
  totalCents: number | null;
  isOnRequest: boolean;
  numberOfAdults: number;
  numberOfChildren: number;
  date: string;
  options: IAncillaryOption[];
  ancillaryItemIndex: number;
  countryCode?: TCountryCode;
}

export const filterAncillaryItems = (items: unknown[]): IPotentialBookingAncillaryItem[] =>
  (items as IPotentialBookingAncillaryItem[]).filter(item => isAncillaryProduct(item.product));

export const buildAddedAncillaryItem = (
  item: IPotentialBookingAncillaryItem,
  keyPrefix: string,
  name: string
): IAddedAncillaryItem => {
  const userSelections = item.ancillaryData?.userSelections;
  const isOnRequest = item.isOnRequest === true || isNil(item.totalCents);
  const totalCents = item.totalCents ?? 0;
  const requestIndex = getAncillaryRequestIndex(item);

  return {
    key: `${keyPrefix}-${requestIndex}`,
    name,
    units: userSelections?.perUnitQuantityOverride ?? 1,
    total: isOnRequest ? null : formatPrice(totalCents / 100),
    totalCents: isOnRequest ? null : totalCents,
    isOnRequest,
    numberOfAdults: userSelections?.numberOfAdults ?? 0,
    numberOfChildren: userSelections?.agesOfAllChildren?.length ?? 0,
    date: userSelections?.selectedDate ?? '',
    options: toAncillaryOptions(item),
    ancillaryItemIndex: requestIndex,
    countryCode: item.ancillaryData?.product?.country?.isoCode as TCountryCode | undefined,
  };
};
